/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { getBackendUrl } from "@src/utils/getBackendUrl";

interface POEvent {
	id: string;
	marketplaceEventId: string;
	marketplace: string;
	eventName: string;
	eventDateTime: string;
	localEventDateTime: string;
	venue: string;
	headliner: string;
	city: string;
	state: string;
	containsPo: boolean;
	poQuantity?: number;
	audit: {
		createdBy: string;
		createdDate: string;
		updatedBy: string;
		updatedDate: string;
	};
}

interface PODataResponse {
	total: number;
	events: Array<POEvent>;
	total_events_count: number;
}

interface MappingSuggestion {
	event: {
		eventName: string;
		eventDate: string;
		eventTime: string;
		venueName: string;
		cityName: string;
		stateName: string;
	};
	score: number;
	mpEventStatus: string;
}

interface MappingSuggestionsResponse {
	total: number;
	suggestions: Array<MappingSuggestion>;
}

interface POState {
	events: Array<POEvent>;
	totalEventsCount: number;
	loading: "idle" | "pending" | "succeeded" | "failed";
	loadingSuggestions: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
	currentPage: number;
	itemsPerPage: number;
	latestRequestId: number | null;
	mappingSuggestions: Array<MappingSuggestion>;
	mpEventStatus: string;
}

const initialState: POState = {
	events: [],
	totalEventsCount: 0,
	loading: "idle",
	loadingSuggestions: "idle",
	error: null,
	currentPage: 1,
	itemsPerPage: 30,
	latestRequestId: null,
	mappingSuggestions: [],
	mpEventStatus: "",
};

export const getPOData = createAsyncThunk(
	"poMapping/getPOData",
	async (
		{
			page,
			pageSize,
			searchValue,
			marketplace,
			startDate,
			endDate,
			sortBy,
			sortDirection,
			requestId,
		}: {
			page: number;
			pageSize: number;
			searchValue?: string;
			marketplace: string;
			startDate?: string;
			endDate?: string;
			sortBy?: string;
			sortDirection?: "asc" | "desc";
			requestId: number;
		},
		{ rejectWithValue },
	) => {
		try {
			const params: Record<string, any> = {
				page,
				pageSize,
				marketplace,
				...(searchValue && { searchValue }),
				...(startDate && { startDate }),
				...(endDate && { endDate }),
				...(sortBy && { sortBy }),
				...(sortDirection && { sortDirection }),
			};

			// Filter out null/undefined/empty values
			const filteredParams = Object.fromEntries(
				Object.entries(params).filter(([_, value]) => value !== null && value !== undefined && value !== ""),
			);

			const response = await axios.get(`${getBackendUrl()}/get-unmapped-po-events`, {
				params: filteredParams,
				headers: getAuthHeaders(),
			});

			return { ...response.data, requestId };
		} catch (error) {
			return rejectWithValue("Failed to fetch PO events: " + String(error));
		}
	},
);

export const getPOMappingSuggestions = createAsyncThunk<
	MappingSuggestionsResponse,
	{
		marketplace: string;
		eventName: string;
		eventDateTime: string;
		venue: string;
		city?: string;
		state?: string;
	},
	{ rejectValue: string }
>(
	"poMapping/getPOMappingSuggestions",
	async (params, { rejectWithValue }) => {
		try {
			const { marketplace, eventName, eventDateTime, venue, city, state } = params;

			const queryParams: Record<string, any> = {
				marketplace,
				eventName,
				eventDateTime,
				venue,
				...(city && { city }),
				...(state && { state }),
			};

			const response = await axios.get(`${getBackendUrl()}/marketplace-mapping-suggestions`, {
				params: queryParams,
				headers: getAuthHeaders(),
			});

			return response.data;
		} catch (error) {
			return rejectWithValue("Failed to fetch mapping suggestions: " + String(error));
		}
	},
);

export const poMappingSlice = createSlice({
	name: "poMapping",
	initialState,
	reducers: {
		setCurrentPage: (state, action) => {
			state.currentPage = action.payload;
		},
		setItemsPerPage: (state, action) => {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getPOData.pending, (state, action) => {
				state.loading = "pending";
				state.error = null;
				state.latestRequestId = action.meta.arg.requestId;
			})
			.addCase(getPOData.fulfilled, (state, action) => {
				// Only apply data if requestId matches the latestRequestId in state
				if (action.payload.requestId !== state.latestRequestId) return;

				state.loading = "succeeded";
				state.events = action.payload.events;
				state.totalEventsCount = action.payload.total_events_count;
			})
			.addCase(getPOData.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
				state.events = [];
				state.totalEventsCount = 0;
			})
			.addCase(getPOMappingSuggestions.pending, (state) => {
				state.loadingSuggestions = "pending";
				state.error = null;
			})
			.addCase(getPOMappingSuggestions.fulfilled, (state, action) => {
				state.loadingSuggestions = "succeeded";
				state.mappingSuggestions = action.payload.suggestions;
			})
			.addCase(getPOMappingSuggestions.rejected, (state, action) => {
				state.loadingSuggestions = "failed";
				state.error = action.payload as string;
				state.mappingSuggestions = [];
			});
	},
});

export const { setCurrentPage, setItemsPerPage } = poMappingSlice.actions;

export default poMappingSlice.reducer;
